/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { handleApiResponse } from "../apiResponse";
import { createQueryOptions, createMutationFn } from "@shared/lib/api";
import { PromptProcessingResult } from "@shared/types/responses/llm";
import {
  ExecuteActionPlanResponseData,
  ActionPlanStatusResponseData
} from "@shared/types/responses/learning";
import { LearningPathActionPlan } from "@shared/types/learning";
import { LearningActionPlanExecution } from "@shared/schema/learning";
import { authenticatedFetch } from "@shared/lib/api";

// ============================================================================
// Execution Record Management API Functions
// ============================================================================

/**
 * Creates a new action plan execution record
 * @param userRequest The user's natural language request
 * @returns Promise with the created execution ID
 */
export async function createExecutionRecord(userRequest: string): Promise<{ executionId: string }> {
  const response = await authenticatedFetch("/api/learning/action-plans/executions", {
    method: "POST",
    body: { userRequest }
  });

  if (!response.ok) {
    throw new Error("Failed to create execution record");
  }

  return handleApiResponse<{ executionId: string }>(response);
}

/**
 * React Query hook for creating execution records
 */
export function useCreateExecutionRecord() {
  return useMutation<{ executionId: string }, Error, string>({
    mutationFn: createExecutionRecord,
  });
}

// ============================================================================
// Request Translation API Functions
// ============================================================================

/**
 * Request body for the request translation endpoint
 */
export interface TranslateRequestBody {
  userRequest: string;
  processingId?: string;
}

export interface TranslateWithFileRequestBody {
  userRequest: string;
  processingId?: string;
  file: File;
}

/**
 * Translates a natural language user request into a structured learning action plan
 * @param userRequest The natural language request from the user
 * @param processingId Optional processing ID for tracking progress
 * @returns Promise with the prompt processing result containing the action plan
 */
export async function translateUserRequest(userRequest: string, processingId?: string): Promise<PromptProcessingResult> {
  // Create an AbortController to handle timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 120000); // 120 second timeout

  try {
    const response = await authenticatedFetch("/api/learning/action-plans/translate", {
      method: "POST",
      body: { userRequest, processingId }
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error("Failed to translate user request");
    }

    return handleApiResponse<PromptProcessingResult>(response);
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error("Request timed out after 120 seconds");
    }
    throw error;
  }
}

/**
 * Translates a natural language user request with file attachment into a structured learning action plan
 * @param userRequest The natural language request from the user
 * @param file The file to attach as context
 * @param processingId Optional processing ID for tracking progress
 * @returns Promise with the prompt processing result containing the action plan
 */
export async function translateUserRequestWithFile(userRequest: string, file: File, processingId?: string): Promise<PromptProcessingResult> {
  // Create an AbortController to handle timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 120000); // 120 second timeout

  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('userRequest', userRequest);
    if (processingId) {
      formData.append('processingId', processingId);
    }

    const response = await authenticatedFetch("/api/learning/action-plans/translate-with-file", {
      method: "POST",
      body: formData,
      skipJsonHeaders: true, // Important: Don't set Content-Type for FormData
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error("Failed to translate user request with file");
    }

    return handleApiResponse<PromptProcessingResult>(response);
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error("Request timed out after 120 seconds");
    }
    throw error;
  }
}

/**
 * React Query hook for translating user requests
 */
export function useTranslateUserRequest() {
  return useMutation<PromptProcessingResult, Error, { userRequest: string; processingId?: string }>({
    mutationFn: ({ userRequest, processingId }) => translateUserRequest(userRequest, processingId),
  });
}

/**
 * React Query hook for translating user requests with file attachments
 */
export function useTranslateUserRequestWithFile() {
  return useMutation<PromptProcessingResult, Error, { userRequest: string; file: File; processingId?: string }>({
    mutationFn: ({ userRequest, file, processingId }) => translateUserRequestWithFile(userRequest, file, processingId),
  });
}

// ============================================================================
// Action Plan Execution API Functions
// ============================================================================

/**
 * Executes a learning action plan by plan ID
 * @param planId The ID of the action plan to execute
 * @param executionId Optional execution ID to use existing execution record
 * @returns Promise with the execution result
 */
export async function executeActionPlan(planId: string, executionId?: string): Promise<ExecuteActionPlanResponseData> {
  const response = await authenticatedFetch(`/api/learning/action-plans/${planId}/execute`, {
    method: "POST",
    body: executionId ? { executionId } : undefined
  });

  if (!response.ok) {
    throw new Error("Failed to execute action plan");
  }

  return handleApiResponse<ExecuteActionPlanResponseData>(response);
}

/**
 * React Query hook for executing action plans
 */
export function useExecuteActionPlan() {
  return useMutation<ExecuteActionPlanResponseData, Error, { planId: string; executionId?: string }>({
    mutationFn: ({ planId, executionId }) => executeActionPlan(planId, executionId),
  });
}

// ============================================================================
// Action Plan Status API Functions
// ============================================================================

/**
 * Gets the status of an action plan execution by plan ID
 * @param planId The ID of the action plan
 * @returns Promise with the execution status
 */
export async function getActionPlanStatus(planId: string): Promise<ActionPlanStatusResponseData> {
  const response = await authenticatedFetch(`/api/learning/action-plans/${planId}/status`);

  if (!response.ok) {
    throw new Error(`Failed to get status for action plan ${planId}`);
  }

  return handleApiResponse<ActionPlanStatusResponseData>(response);
}

/**
 * Gets the status of an action plan execution by execution ID
 * @param executionId The ID of the execution
 * @returns Promise with the execution status
 */
export async function getExecutionStatus(executionId: string): Promise<ActionPlanStatusResponseData> {
  const response = await authenticatedFetch(`/api/learning/action-plans/executions/${executionId}/status`);

  if (!response.ok) {
    throw new Error(`Failed to get status for execution ${executionId}`);
  }

  return handleApiResponse<ActionPlanStatusResponseData>(response);
}

/**
 * Gets the detailed execution results by execution ID
 * @param executionId The ID of the execution
 * @returns Promise with the execution results
 */
export async function getExecutionResults(executionId: string): Promise<ExecuteActionPlanResponseData> {
  const response = await authenticatedFetch(`/api/learning/action-plans/executions/${executionId}/results`);

  if (!response.ok) {
    throw new Error(`Failed to get results for execution ${executionId}`);
  }

  return handleApiResponse<ExecuteActionPlanResponseData>(response);
}

/**
 * React Query hook for getting action plan status by plan ID
 */
export function useActionPlanStatus(planId: string, options?: { enabled?: boolean; refetchInterval?: number }) {
  return useQuery({
    ...createQueryOptions<ActionPlanStatusResponseData>(`/api/learning/action-plans/${planId}/status`),
    enabled: options?.enabled !== false && !!planId,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * React Query hook for getting execution status by execution ID
 */
export function useExecutionStatus(executionId: string, options?: { enabled?: boolean; refetchInterval?: number }) {
  return useQuery({
    ...createQueryOptions<ActionPlanStatusResponseData>(`/api/learning/action-plans/executions/${executionId}/status`),
    enabled: options?.enabled !== false && !!executionId,
    refetchInterval: options?.refetchInterval,
  });
}

// ============================================================================
// Action Plan Management API Functions
// ============================================================================

/**
 * Cancels an action plan execution
 * @param planId The ID of the action plan to cancel
 * @returns Promise with cancellation result
 */
export async function cancelActionPlan(planId: string): Promise<{ cancelled: boolean }> {
  const response = await authenticatedFetch(`/api/learning/action-plans/${planId}/cancel`, {
    method: "POST"
  });

  if (!response.ok) {
    throw new Error(`Failed to cancel action plan ${planId}`);
  }

  return handleApiResponse<{ cancelled: boolean }>(response);
}

/**
 * Cancels an action plan execution by execution ID
 * @param executionId The ID of the execution to cancel
 * @returns Promise with cancellation result
 */
export async function cancelExecution(executionId: string): Promise<{ cancelled: boolean }> {
  const response = await authenticatedFetch(`/api/learning/action-plans/executions/${executionId}/cancel`, {
    method: "POST"
  });

  if (!response.ok) {
    throw new Error(`Failed to cancel execution ${executionId}`);
  }

  return handleApiResponse<{ cancelled: boolean }>(response);
}

/**
 * React Query hook for cancelling action plans
 */
export function useCancelActionPlan() {
  return useMutation<{ cancelled: boolean }, Error, string>({
    mutationFn: cancelActionPlan,
  });
}

/**
 * React Query hook for cancelling executions by execution ID
 */
export function useCancelExecution() {
  return useMutation<{ cancelled: boolean }, Error, string>({
    mutationFn: cancelExecution,
  });
}

// ============================================================================
// Combined Workflow Functions
// ============================================================================

/**
 * Convenience function that translates a user request and returns the action plan
 * Extracts the action plan from the tool execution result
 * @param userRequest The natural language request from the user
 * @param file Optional file to attach as context
 * @returns Promise with the action plan or null if no plan was generated
 */
export async function translateAndExtractActionPlan(userRequest: string, file?: File): Promise<LearningPathActionPlan | null> {
  const result = file 
    ? await translateUserRequestWithFile(userRequest, file)
    : await translateUserRequest(userRequest);
  
  // Extract action plan from tool execution result
  if (result.toolExecution?.result?.actionPlan) {
    return result.toolExecution.result.actionPlan as LearningPathActionPlan;
  }
  
  // Fallback: try to extract from data field
  if (result.data?.actionPlan) {
    return result.data.actionPlan as LearningPathActionPlan;
  }
  
  return null;
}

/**
 * Extracts the planId from the translation result's tool execution
 * @param translationResult The result from translateUserRequest
 * @returns The planId or null if not found
 */
export function extractPlanIdFromTranslationResult(translationResult: PromptProcessingResult): string | null {
  // Try to get planId from tool execution result
  if (translationResult.toolExecution?.result?.planId) {
    return translationResult.toolExecution.result.planId;
  }
  
  // Fallback: try to extract from action plan
  if (translationResult.toolExecution?.result?.actionPlan?.planId) {
    return translationResult.toolExecution.result.actionPlan.planId;
  }
  
  return null;
}

/**
 * Convenience function that translates a user request and immediately executes the resulting action plan
 * @param userRequest The natural language request from the user
 * @param processingId Optional processing ID for tracking progress
 * @returns Promise with the execution result, or null if no plan was generated
 */
export async function translateAndExecute(userRequest: string, processingId?: string): Promise<ExecuteActionPlanResponseData | null> {
  const translationResult = await translateUserRequest(userRequest, processingId);
  
  const planId = extractPlanIdFromTranslationResult(translationResult);
  
  if (!planId) {
    return null;
  }
  
  return executeActionPlan(planId);
}

/**
 * React Query hook for the combined translate and execute workflow
 */
export function useTranslateAndExecute() {
  return useMutation<ExecuteActionPlanResponseData | null, Error, { userRequest: string; processingId?: string }>({
    mutationFn: ({ userRequest, processingId }) => translateAndExecute(userRequest, processingId),
  });
}

// ============================================================================
// Polling Utilities
// ============================================================================

/**
 * Polls execution status until completion or failure
 * @param executionId The execution ID to poll
 * @param options Polling options
 * @returns Promise that resolves when execution is complete
 */
export async function pollExecutionStatus(
  executionId: string,
  options: {
    intervalMs?: number;
    maxAttempts?: number;
    onProgress?: (status: ActionPlanStatusResponseData) => void;
  } = {}
): Promise<ActionPlanStatusResponseData> {
  const { intervalMs = 1000, maxAttempts = 120, onProgress } = options;
  
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const status = await getExecutionStatus(executionId);
    
    if (onProgress) {
      onProgress(status);
    }
    
    // Check if execution is complete
    if (status.status === 'completed' || status.status === 'failed' || status.status === 'cancelled') {
      return status;
    }
    
    // Wait before next poll
    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }
  
  throw new Error(`Execution polling timed out after ${maxAttempts} attempts`);
}

/**
 * React Query hook that automatically polls execution status
 * @param executionId The execution ID to poll
 * @param options Polling options including onComplete callback
 */
export function usePollExecutionStatus(
  executionId: string,
  options: {
    enabled?: boolean;
    intervalMs?: number;
    onComplete?: (status: ActionPlanStatusResponseData) => void;
  } = {}
) {
  const { enabled = true, intervalMs = 1000, onComplete } = options;
  
  return useQuery({
    queryKey: ['execution-status', executionId],
    queryFn: () => getExecutionStatus(executionId),
    enabled: enabled && !!executionId,
    refetchInterval: (query) => {
      // Stop polling if execution is complete
      const data = query.state.data;
      if (data && (data.status === 'completed' || data.status === 'failed' || data.status === 'cancelled')) {
        if (onComplete) {
          onComplete(data);
        }
        return false;
      }
      return intervalMs;
    },
    refetchIntervalInBackground: true,
  });
}

export async function getActionPlanExecutionHistory(): Promise<LearningActionPlanExecution[]> {
  // Create an AbortController to handle timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

  try {
    const response = await authenticatedFetch("/api/learning/action-plans/executions");

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error("Failed to fetch action plan execution history");
    }

    return handleApiResponse<LearningActionPlanExecution[]>(response);
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timeout: Failed to fetch action plan execution history');
    }
    throw error;
  }
} 